/**
 * WS4 Data Tables modal — legacy `.modal-shell` chrome (dark bg +
 * true-blue border + halo, 12 px radius) hosting a tabbed view of the
 * full WS4 Human-Agent ways-of-working registry. Each tab renders one
 * or two sections of tables and (for Overview) the BU adoption bar
 * grid, matching the legacy markup section-for-section.
 *
 * Ported from:
 *   - `_legacy/ws4-assetlocker.js` lines 109-294 — `#dataTablesModal`
 *     shell, tab bar, and per-pane content (verbatim cell-for-cell).
 *   - `_legacy/xaes.css` lines 1060-1423 — chrome and `.dt-*` classes
 *     (already ported to `src/index.css`; `.dt-bar-*` appended for the
 *     adoption-bar grid in the Overview pane).
 *
 * The vanilla version was static markup with `onclick="dtActivateTab(…)"`
 * inline; here local React state drives the same `.dt-tab-active`
 * class swap. Only one pane mounts at a time (we render the active key
 * directly), so the rendered `<div>` always carries `dt-pane-active`.
 * `PanelDialogContent` bypasses shadcn's built-in top-right ✕ so the
 * legacy `.modal-close-btn` in the header is the sole close affordance.
 */

import { useState, type CSSProperties } from "react";
import { Table2, X } from "lucide-react";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { PanelDialogContent } from "@/components/shell/PanelDialogContent";
import { cn } from "@/lib/utils";
import {
  getDataTables,
  type AdoptionBar,
  type AllocationAccent,
  type CollaborationModel,
  type DtAccent,
  type HandoffStandard,
  type HandoffTrigger,
  type KpiCard,
  type ModeTransition,
  type OperatingMode,
  type PerfBenchmark,
  type RoleAllocation,
  type RoleDefinition,
  type SignalThreshold,
  type WorkshopDataTables,
} from "./data-tables-content";
import { useGeneratedDataTables } from "./use-generated-data-tables";

type DataTablesModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workshopId: string;
};

type TabKey = "overview" | "modes" | "roles" | "handoff" | "metrics";

const TABS: ReadonlyArray<{ key: TabKey; label: string }> = [
  { key: "overview", label: "Overview" },
  { key: "modes", label: "Operating Modes" },
  { key: "roles", label: "Roles & Responsibilities" },
  { key: "handoff", label: "Handoff Protocols" },
  { key: "metrics", label: "Performance Metrics" },
];

/* --------------------------- Accent maps -------------------------- */

/**
 * Accent slot → `.dt-pill-*` class. Mirrors `_legacy/xaes.css` lines
 * 1384-1389. Legacy CSS aliases melon/purple/red to the same swatch;
 * we keep the semantic class names so future theme work can split them
 * without changing this module.
 */
const ACCENT_PILL: Record<DtAccent, string> = {
  orange: "dt-pill dt-pill-orange",
  blue: "dt-pill dt-pill-blue",
  green: "dt-pill dt-pill-green",
  cyan: "dt-pill dt-pill-cyan",
  melon: "dt-pill dt-pill-melon",
  purple: "dt-pill dt-pill-purple",
  red: "dt-pill dt-pill-red",
  gold: "dt-pill dt-pill-orange",
  sky: "dt-pill dt-pill-cyan",
  "true-blue": "dt-pill dt-pill-blue",
};

/** Accent slot → CSS var for coloured text + bar fills. */
function accentVar(accent: DtAccent): string {
  switch (accent) {
    case "blue":
    case "true-blue":
      return "var(--accent-true-blue)";
    case "cyan":
    case "sky":
      return "var(--accent-sky)";
    case "green":
      return "var(--accent-green)";
    case "orange":
    case "gold":
      return "var(--accent-gold)";
    case "melon":
    case "purple":
    case "red":
      return "var(--accent-melon)";
  }
}

function allocationColor(accent: AllocationAccent): string {
  switch (accent) {
    case "green":
      return "var(--accent-green)";
    case "gold":
      return "var(--accent-gold)";
    case "muted":
      return "var(--text-muted)";
  }
}

const CENTER: CSSProperties = { textAlign: "center" };
const SECTION_GAP: CSSProperties = { marginTop: 16 };

/* ----------------------------- KPI row --------------------------- */

function KpiGrid({ kpis }: { kpis: KpiCard[] }) {
  return (
    <div className="dt-kpi-grid">
      {kpis.map((k) => (
        <div key={k.label} className="dt-kpi-card">
          <div className="dt-kpi-label">{k.label}</div>
          <div className="dt-kpi-value" style={{ color: accentVar(k.accent) }}>
            {k.value}
          </div>
          <div className="dt-kpi-sub">{k.sub}</div>
        </div>
      ))}
    </div>
  );
}

/* --------------------------- Overview ---------------------------- */

function OverviewPane({
  kpis,
  models,
  adoption,
}: {
  kpis: KpiCard[];
  models: CollaborationModel[];
  adoption: AdoptionBar[];
}) {
  const half = Math.ceil(adoption.length / 2);
  const left = adoption.slice(0, half);
  const right = adoption.slice(half);

  return (
    <div className="dt-pane dt-pane-active">
      <KpiGrid kpis={kpis} />

      <div className="dt-section-title">Collaboration Model Summary</div>
      <table className="dt-table">
        <thead>
          <tr>
            <th>Model</th>
            <th>Human Role</th>
            <th>Agent Role</th>
            <th>Decision Authority</th>
            <th>Typical Use Cases</th>
            <th>Maturity</th>
          </tr>
        </thead>
        <tbody>
          {models.map((m) => (
            <tr key={m.model}>
              <td>
                <span className={ACCENT_PILL[m.modelAccent]}>{m.model}</span>
              </td>
              <td>{m.humanRole}</td>
              <td>{m.agentRole}</td>
              <td
                style={{
                  color: accentVar(m.authorityAccent),
                  fontWeight: 600,
                }}
              >
                {m.authority}
              </td>
              <td>{m.useCases}</td>
              <td>
                <span className={ACCENT_PILL[m.maturityAccent]}>
                  {m.maturity}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="dt-section-title" style={SECTION_GAP}>
        Current Adoption by Business Unit
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 8,
        }}
      >
        <AdoptionColumn rows={left} />
        <AdoptionColumn rows={right} />
      </div>
    </div>
  );
}

function AdoptionColumn({ rows }: { rows: AdoptionBar[] }) {
  return (
    <div>
      {rows.map((r) => (
        <div key={r.label} className="dt-bar-wrap">
          <div className="dt-bar-label">{r.label}</div>
          <div className="dt-bar-bg">
            <div
              className="dt-bar-fill"
              style={{
                width: `${r.pct}%`,
                background: accentVar(r.accent),
              }}
            />
          </div>
          <div className="dt-bar-pct">{r.pct}%</div>
        </div>
      ))}
    </div>
  );
}

/* ----------------------- Operating Modes ------------------------- */

function ModesPane({
  modes,
  transitions,
}: {
  modes: OperatingMode[];
  transitions: ModeTransition[];
}) {
  return (
    <div className="dt-pane dt-pane-active">
      <div className="dt-section-title">Operating Mode Specifications</div>
      <table className="dt-table">
        <thead>
          <tr>
            <th>Mode</th>
            <th>Trigger Condition</th>
            <th>Latency Target</th>
            <th>Human Touch Points</th>
            <th>Fallback Protocol</th>
            <th>Risk Level</th>
          </tr>
        </thead>
        <tbody>
          {modes.map((m) => (
            <tr key={m.mode}>
              <td
                style={{ color: accentVar(m.modeAccent), fontWeight: 600 }}
              >
                {m.mode}
              </td>
              <td>{m.trigger}</td>
              <td>{m.latency}</td>
              <td>{m.touchPoints}</td>
              <td>{m.fallback}</td>
              <td>
                <span className={ACCENT_PILL[m.riskAccent]}>{m.risk}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="dt-section-title" style={SECTION_GAP}>
        Mode Transition Rules
      </div>
      <table className="dt-table">
        <thead>
          <tr>
            <th>From Mode</th>
            <th>To Mode</th>
            <th>Condition</th>
            <th>Required Approver</th>
            <th>Cooling Period</th>
          </tr>
        </thead>
        <tbody>
          {transitions.map((t, idx) => (
            <tr key={`${t.from}-${t.to}-${idx}`}>
              <td>
                {t.fromAccent ? (
                  <span className={ACCENT_PILL[t.fromAccent]}>{t.from}</span>
                ) : (
                  t.from
                )}
              </td>
              <td>
                <span className={ACCENT_PILL[t.toAccent]}>{t.to}</span>
              </td>
              <td>{t.condition}</td>
              <td>{t.approver}</td>
              <td>{t.cooling}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ----------------------------- Roles ----------------------------- */

function RolesPane({
  definitions,
  allocations,
}: {
  definitions: RoleDefinition[];
  allocations: RoleAllocation[];
}) {
  return (
    <div className="dt-pane dt-pane-active">
      <div className="dt-section-title">Human Agent Role Definitions</div>
      <table className="dt-table">
        <thead>
          <tr>
            <th>Role</th>
            <th>Primary Responsibilities</th>
            <th>Agent Interaction</th>
            <th>Decision Authority</th>
            <th>Skill Requirements</th>
          </tr>
        </thead>
        <tbody>
          {definitions.map((r) => (
            <tr key={r.role}>
              <td style={{ color: "var(--text-primary)", fontWeight: 600 }}>
                {r.role}
              </td>
              <td>{r.responsibilities}</td>
              <td>{r.interaction}</td>
              <td>{r.authority}</td>
              <td>{r.skills}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="dt-section-title" style={SECTION_GAP}>
        Role Allocation by Operating Mode
      </div>
      <table className="dt-table">
        <thead>
          <tr>
            <th>Role</th>
            <th style={CENTER}>
              <span className="dt-pill dt-pill-orange">IN-THE-LOOP</span>
            </th>
            <th style={CENTER}>
              <span className="dt-pill dt-pill-blue">ABOVE-THE-LOOP</span>
            </th>
            <th style={CENTER}>
              <span className="dt-pill dt-pill-cyan">ON-THE-LOOP</span>
            </th>
            <th style={CENTER}>
              <span className="dt-pill dt-pill-purple">OUT-OF-LOOP</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {allocations.map((a) => (
            <tr key={a.role}>
              <td>{a.role}</td>
              <td style={{ ...CENTER, color: allocationColor(a.inAccent) }}>
                {a.in}
              </td>
              <td style={{ ...CENTER, color: allocationColor(a.aboveAccent) }}>
                {a.above}
              </td>
              <td style={{ ...CENTER, color: allocationColor(a.onAccent) }}>
                {a.on}
              </td>
              <td style={{ ...CENTER, color: allocationColor(a.outAccent) }}>
                {a.out}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------------------- Handoff ---------------------------- */

function HandoffPane({
  triggers,
  standards,
}: {
  triggers: HandoffTrigger[];
  standards: HandoffStandard[];
}) {
  return (
    <div className="dt-pane dt-pane-active">
      <div className="dt-section-title">Agent-to-Human Handoff Triggers</div>
      <table className="dt-table">
        <thead>
          <tr>
            <th>Trigger Category</th>
            <th>Trigger Condition</th>
            <th>Severity</th>
            <th>Notification Channel</th>
            <th>SLA Response</th>
            <th>Auto-Action</th>
          </tr>
        </thead>
        <tbody>
          {triggers.map((t) => (
            <tr key={t.category}>
              <td style={{ fontWeight: 600 }}>{t.category}</td>
              <td>{t.condition}</td>
              <td>
                <span className={ACCENT_PILL[t.severityAccent]}>
                  {t.severity}
                </span>
              </td>
              <td>{t.channel}</td>
              <td>{t.sla}</td>
              <td>{t.auto}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="dt-section-title" style={SECTION_GAP}>
        Human-to-Agent Handoff Standards
      </div>
      <table className="dt-table">
        <thead>
          <tr>
            <th>Handoff Element</th>
            <th>Required Fields</th>
            <th>Format</th>
            <th>Validation Check</th>
          </tr>
        </thead>
        <tbody>
          {standards.map((s) => (
            <tr key={s.element}>
              <td style={{ fontWeight: 600 }}>{s.element}</td>
              <td>{s.required}</td>
              <td>{s.format}</td>
              <td>{s.validation}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* -------------------------- Performance -------------------------- */

function MetricsPane({
  kpis,
  benchmarks,
  signals,
}: {
  kpis: KpiCard[];
  benchmarks: PerfBenchmark[];
  signals: SignalThreshold[];
}) {
  return (
    <div className="dt-pane dt-pane-active">
      <KpiGrid kpis={kpis} />

      <div className="dt-section-title">
        Performance Benchmarks by Operating Mode
      </div>
      <table className="dt-table">
        <thead>
          <tr>
            <th>Metric</th>
            <th style={CENTER}>In-The-Loop</th>
            <th style={CENTER}>Above-The-Loop</th>
            <th style={CENTER}>On-The-Loop</th>
            <th style={CENTER}>Out-Of-Loop</th>
            <th>Target / Threshold</th>
          </tr>
        </thead>
        <tbody>
          {benchmarks.map((b) => (
            <tr key={b.metric}>
              <td>{b.metric}</td>
              <td
                style={{
                  ...CENTER,
                  ...(b.inAccent ? { color: accentVar(b.inAccent) } : {}),
                }}
              >
                {b.inLoop}
              </td>
              <td
                style={{
                  ...CENTER,
                  ...(b.aboveAccent
                    ? { color: accentVar(b.aboveAccent) }
                    : {}),
                }}
              >
                {b.aboveLoop}
              </td>
              <td
                style={{
                  ...CENTER,
                  ...(b.onAccent ? { color: accentVar(b.onAccent) } : {}),
                }}
              >
                {b.onLoop}
              </td>
              <td
                style={{
                  ...CENTER,
                  ...(b.outAccent ? { color: accentVar(b.outAccent) } : {}),
                }}
              >
                {b.outLoop}
              </td>
              <td>{b.target}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="dt-section-title" style={SECTION_GAP}>
        Quality Signal Thresholds
      </div>
      <table className="dt-table">
        <thead>
          <tr>
            <th>Signal</th>
            <th>Green (Normal)</th>
            <th>Amber (Watch)</th>
            <th>Red (Escalate)</th>
            <th>Measurement Frequency</th>
          </tr>
        </thead>
        <tbody>
          {signals.map((s) => (
            <tr key={s.signal}>
              <td>{s.signal}</td>
              <td style={{ color: "var(--accent-green)" }}>{s.green}</td>
              <td style={{ color: "var(--accent-gold)" }}>{s.amber}</td>
              <td style={{ color: "var(--accent-red)" }}>{s.red}</td>
              <td>{s.frequency}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* --------------------------- Empty state ------------------------- */

function EmptyShell({
  workshopId,
  onClose,
  message,
}: {
  workshopId: string;
  onClose: () => void;
  /** Overrides the default "not registered yet" copy — used to distinguish
   *  loading/error states from a genuine "nothing generated yet" empty
   *  state (ADO #688). */
  message?: string;
}) {
  return (
    <div className="modal-shell" style={{ width: "100%", maxWidth: 720 }}>
      <div className="modal-header">
        <div className="flex items-center gap-[10px]">
          <Table2
            aria-hidden
            style={{ width: 14, height: 14, color: "var(--accent-gold)" }}
          />
          <span className="modal-title">DATA TABLES</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="modal-close-btn"
          aria-label="Close"
        >
          <X aria-hidden style={{ width: 12, height: 12 }} />
          CLOSE
        </button>
      </div>
      <div className="modal-body" style={{ padding: 24 }}>
        <p
          style={{
            color: "var(--text-secondary)",
            fontSize: "var(--fs-sub)",
            lineHeight: 1.6,
          }}
        >
          {message ?? (
            <>
              No data tables registered for workshop{" "}
              <code
                style={{
                  background: "rgba(var(--accent-true-blue-rgb), 0.08)",
                  color: "var(--text-primary)",
                  padding: "1px 6px",
                  borderRadius: 4,
                  fontFamily: "var(--font-mono)",
                  fontSize: "var(--fs-base)",
                }}
              >
                {workshopId}
              </code>
              .
            </>
          )}
        </p>
      </div>
      <div className="modal-footer">
        <span className="modal-footer-label">XAES Phase Zero</span>
        <span className="modal-footer-sep">|</span>
        <span className="modal-footer-ws">
          {workshopId} — Human-Agent Ways of Working
        </span>
      </div>
    </div>
  );
}

/* --------------------------- Active pane ------------------------- */

function ActivePane({
  data,
  activeKey,
}: {
  data: WorkshopDataTables;
  activeKey: TabKey;
}) {
  switch (activeKey) {
    case "overview":
      return (
        <OverviewPane
          kpis={data.overviewKpis}
          models={data.collaborationModels}
          adoption={data.adoptionByBu}
        />
      );
    case "modes":
      return (
        <ModesPane
          modes={data.operatingModes}
          transitions={data.modeTransitions}
        />
      );
    case "roles":
      return (
        <RolesPane
          definitions={data.roleDefinitions}
          allocations={data.roleAllocations}
        />
      );
    case "handoff":
      return (
        <HandoffPane
          triggers={data.handoffTriggers}
          standards={data.handoffStandards}
        />
      );
    case "metrics":
      return (
        <MetricsPane
          kpis={data.performanceKpis}
          benchmarks={data.performanceBenchmarks}
          signals={data.signalThresholds}
        />
      );
  }
}

/* --------------------------- Component --------------------------- */

export function DataTablesModal({
  open,
  onOpenChange,
  workshopId,
}: DataTablesModalProps) {
  // ADO #688 — a real, generated Data Tables view (from this workshop's
  // actual actions/findings/transcript) takes priority when the BFF has
  // produced one; the static WS4 fixture is now purely a fallback for WS4
  // when nothing has been generated yet, never a stand-in for a real
  // workshop's data.
  const generated = useGeneratedDataTables(workshopId);
  const data = generated.data ?? getDataTables(workshopId);
  const [activeKey, setActiveKey] = useState<TabKey>("overview");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PanelDialogContent
        className="border-0 bg-transparent p-0 shadow-none"
        style={{
          outline: "none",
          width: "90vw",
          maxWidth: 1100,
          maxHeight: "calc(var(--os-vh) * 100 - 56px - 112px - 24px)",
        }}
      >
        <DialogTitle className="sr-only">Data Tables</DialogTitle>
        <DialogDescription className="sr-only">
          WS4 collaboration model registry — {workshopId}
        </DialogDescription>

        {generated.loading ? (
          <EmptyShell
            workshopId={workshopId}
            onClose={() => onOpenChange(false)}
            message="Loading generated data tables…"
          />
        ) : !data ? (
          <EmptyShell
            workshopId={workshopId}
            onClose={() => onOpenChange(false)}
            message={
              generated.error
                ? "Couldn't load the generated data tables — try again shortly."
                : undefined
            }
          />
        ) : (
          <div
            className="modal-shell"
            style={{
              width: "100%",
              maxHeight: "calc(var(--os-vh) * 100 - 56px - 112px - 24px)",
            }}
          >
            {/* Header */}
            <div className="modal-header">
              <div className="flex items-center gap-[10px]">
                <Table2
                  aria-hidden
                  style={{ width: 14, height: 14, color: "var(--accent-gold)" }}
                />
                <span className="modal-title">DATA TABLES</span>
                <span
                  style={{
                    padding: "1px 8px",
                    background: "rgba(var(--accent-gold-rgb), 0.12)",
                    border: "1px solid rgba(var(--accent-gold-rgb), 0.35)",
                    borderRadius: 10,
                    fontSize: "var(--fs-micro)",
                    color: "var(--accent-gold)",
                    fontWeight: 600,
                    letterSpacing: 1,
                  }}
                >
                  ASSET LOCKER
                </span>
                <span
                  style={{
                    padding: "1px 8px",
                    background: "rgba(var(--accent-sky-rgb), 0.08)",
                    border: "1px solid rgba(var(--accent-sky-rgb), 0.25)",
                    borderRadius: 10,
                    fontSize: "var(--fs-micro)",
                    color: "var(--accent-sky)",
                    fontWeight: 600,
                    letterSpacing: 1,
                  }}
                >
                  HUMAN-AGENT WAYS OF WORKING
                </span>
              </div>
              <div className="flex items-center gap-[6px]">
                <span
                  aria-hidden
                  style={{
                    display: "inline-block",
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: "var(--accent-green)",
                    boxShadow: "var(--glow-green)",
                  }}
                />
                <span
                  style={{
                    fontSize: "var(--fs-micro)",
                    color: "var(--text-muted)",
                    letterSpacing: 2,
                    textTransform: "uppercase",
                    marginRight: 12,
                  }}
                >
                  Live Data
                </span>
                <button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  className="modal-close-btn"
                  aria-label="Close"
                >
                  <X aria-hidden style={{ width: 12, height: 12 }} />
                  CLOSE
                </button>
              </div>
            </div>

            {/* Tab bar — KPI grid lives INSIDE each pane, not here. */}
            <div
              className="dt-tab-bar"
              role="tablist"
              aria-label="Data tables sections"
            >
              {TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={activeKey === t.key}
                  onClick={() => setActiveKey(t.key)}
                  className={cn("dt-tab", activeKey === t.key && "dt-tab-active")}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Body */}
            <div className="modal-body">
              <ActivePane data={data} activeKey={activeKey} />
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <span className="modal-footer-label">XAES Phase Zero</span>
              <span className="modal-footer-sep">|</span>
              <span className="modal-footer-ws">
                Asset Locker — Data Tables
              </span>
              <span className="modal-footer-sep">|</span>
              <span
                className="modal-footer-ws"
                style={{ color: "var(--accent-sky)" }}
              >
                Human-Agent Ways of Working
              </span>
              <span className="modal-footer-right">
                4 MODES · 6 ROLES · 7 ESCALATION TRIGGERS
              </span>
            </div>
          </div>
        )}
      </PanelDialogContent>
    </Dialog>
  );
}
